export const facebookAppId = process.env.REACT_APP_CLUBHOUSE_FACEBOOK_APP_ID?.trim() ?? '';
export const facebookConfigured = facebookAppId.length > 0;

interface FacebookLoginResponse {
  authResponse?: { accessToken?: string };
  status?: string;
}

interface FacebookSdk {
  init(options: { appId: string; cookie: boolean; xfbml: boolean; version: string }): void;
  login(callback: (response: FacebookLoginResponse) => void, options: { scope: string }): void;
  logout(callback: () => void): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
  }
}

let sdkPromise: Promise<FacebookSdk> | undefined;

function loadFacebookSdk(): Promise<FacebookSdk> {
  if (!facebookConfigured) return Promise.reject(new Error('Facebook sign-in is not configured.'));
  if (window.FB) return Promise.resolve(window.FB);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new Error('Facebook SDK did not load.')), 10_000);
    const initialize = () => {
      if (!window.FB) return;
      window.clearTimeout(timeout);
      window.FB.init({ appId: facebookAppId, cookie: true, xfbml: false, version: 'v23.0' });
      resolve(window.FB);
    };

    const existing = document.getElementById('facebook-jssdk') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', initialize, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.onload = initialize;
    script.onerror = () => reject(new Error('Facebook SDK could not be loaded.'));
    document.head.appendChild(script);
  });
  return sdkPromise;
}

export async function getFacebookAccessToken(): Promise<string> {
  const sdk = await loadFacebookSdk();
  return new Promise((resolve, reject) => {
    sdk.login((response) => {
      const accessToken = response.authResponse?.accessToken;
      if (accessToken) resolve(accessToken);
      else reject(new Error('Facebook sign-in was cancelled or unavailable.'));
    }, { scope: 'public_profile,email' });
  });
}

export function facebookLogout(): void {
  if (window.FB) window.FB.logout(() => undefined);
}